import { useMemo, useState } from "react";

const INITIAL_FORM = {
  name: "",
  supplierType: "supplier",
  contactName: "",
  email: "",
  phone: "",
  notes: "",
};

export default function ClientSuppliersTab({
  suppliers = [],
  loading = false,
  onCreateSupplier,
  onUpdateSupplier,
}) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState("");

  const sortedSuppliers = useMemo(
    () =>
      [...suppliers].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "nl", { sensitivity: "base" })
      ),
    [suppliers]
  );

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      name: String(form.name || "").trim(),
      supplierType: String(form.supplierType || "supplier").trim(),
      contactName: String(form.contactName || "").trim(),
      email: String(form.email || "").trim(),
      phone: String(form.phone || "").trim(),
      notes: String(form.notes || "").trim(),
    };
    if (!payload.name) return;

    if (editingId) {
      await onUpdateSupplier?.(editingId, payload);
    } else {
      await onCreateSupplier?.(payload);
    }
    setEditingId("");
    setForm(INITIAL_FORM);
  };

  const startEdit = (supplier) => {
    setEditingId(String(supplier?._id || supplier?.id || ""));
    setForm({
      name: String(supplier?.name || ""),
      supplierType: String(supplier?.supplierType || "supplier"),
      contactName: String(supplier?.contactName || ""),
      email: String(supplier?.email || ""),
      phone: String(supplier?.phone || ""),
      notes: String(supplier?.notes || ""),
    });
  };

  const cancelEdit = () => {
    setEditingId("");
    setForm(INITIAL_FORM);
  };

  const toggleActive = async (supplier) => {
    const supplierId = String(supplier?._id || supplier?.id || "");
    if (!supplierId) return;
    await onUpdateSupplier?.(supplierId, { active: supplier?.active === false });
  };

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900">Leveranciers van deze klant</h3>
        <p className="mt-1 text-xs text-slate-500">
          Voeg leveranciers toe die specifiek bij deze klant horen, zodat inkoop sneller gaat.
        </p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-slate-900">
          {editingId ? "Leverancier bewerken" : "Nieuwe leverancier"}
        </h4>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700">
            Naam
            <input
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Bijv. Technische Unie"
              required
            />
          </label>
          <label className="text-sm text-slate-700">
            Type
            <select
              value={form.supplierType}
              onChange={(e) => setForm((prev) => ({ ...prev, supplierType: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="supplier">Leverancier</option>
              <option value="zzp">ZZP</option>
              <option value="subcontractor">Onderaannemer</option>
            </select>
          </label>
          <label className="text-sm text-slate-700">
            Contactpersoon
            <input
              value={form.contactName}
              onChange={(e) => setForm((prev) => ({ ...prev, contactName: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Naam"
            />
          </label>
          <label className="text-sm text-slate-700">
            E-mail
            <input
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="leverancier@voorbeeld.nl"
            />
          </label>
          <label className="text-sm text-slate-700">
            Telefoon
            <input
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="+31..."
            />
          </label>
          <label className="text-sm text-slate-700 md:col-span-2">
            Notities
            <input
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Bijzonderheden voor deze klant"
            />
          </label>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {loading ? "Opslaan..." : editingId ? "Opslaan" : "Leverancier toevoegen"}
        </button>
        {editingId ? (
          <button
            type="button"
            onClick={cancelEdit}
            className="ml-2 mt-3 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
          >
            Annuleren
          </button>
        ) : null}
      </form>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Naam</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Contact</th>
              <th className="px-4 py-2">E-mail</th>
              <th className="px-4 py-2">Telefoon</th>
              <th className="px-4 py-2">Notitie</th>
              <th className="px-4 py-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedSuppliers.length === 0 ? (
              <tr>
                <td className="px-4 py-4 text-slate-500" colSpan={8}>
                  Nog geen leveranciers gekoppeld aan deze klant.
                </td>
              </tr>
            ) : (
              sortedSuppliers.map((item) => (
                <tr key={String(item?._id || item?.name)} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{item?.name || "-"}</td>
                  <td className="px-4 py-2 text-slate-700">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                        item?.active === false
                          ? "border-amber-200 bg-amber-50 text-amber-800"
                          : "border-emerald-200 bg-emerald-50 text-emerald-800"
                      }`}
                    >
                      {item?.active === false ? "Inactief" : "Actief"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{item?.supplierType || "-"}</td>
                  <td className="px-4 py-2 text-slate-700">{item?.contactName || "-"}</td>
                  <td className="px-4 py-2 text-slate-700">{item?.email || "-"}</td>
                  <td className="px-4 py-2 text-slate-700">{item?.phone || "-"}</td>
                  <td className="px-4 py-2 text-slate-600">{item?.notes || "-"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => startEdit(item)}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-50"
                    >
                      Bewerken
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => toggleActive(item)}
                      className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-50"
                    >
                      {item?.active === false ? "Activeer" : "Archiveer"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
